import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdCheck, MdVisibility } from 'react-icons/md';
import { THEME, SPRING } from '../theme';
import SwatchPreviewModal from './SwatchPreviewModal';

interface SwatchCircleProps {
  url: string;
  label?: string;
  selected: boolean;
  onClick: () => void;
  size?: number;
}

const SwatchCircle: React.FC<SwatchCircleProps> = ({ url, label, selected, onClick, size = 48 }) => {
  const [hovered, setHovered] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  return (
    <>
      <motion.div
        className="flex flex-col items-center gap-1.5"
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
      >
        <button onClick={onClick} className="relative cursor-pointer group">
          <div
            className="rounded-full overflow-hidden transition-all duration-300"
            style={{
              width: size,
              height: size,
              boxShadow: selected ? `0 0 0 2.5px #121214, 0 0 0 4.5px ${THEME.primary}, ${THEME.primaryGlow}` : 'none',
              border: selected ? 'none' : '1.5px solid rgba(255,255,255,0.12)',
            }}
          >
            <img
              src={url}
              alt={label || 'swatch'}
              className={`w-full h-full object-cover transition-all duration-300 ${selected ? 'brightness-100' : 'brightness-75 group-hover:brightness-95'}`}
              loading="lazy"
            />
          </div>

          {/* Eye icon on hover - top right */}
          <AnimatePresence>
            {hovered && (
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={SPRING.snappy}
                className="absolute -top-1.5 -right-1.5 z-20"
              >
                <motion.div
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewOpen(true);
                  }}
                  className="flex items-center justify-center cursor-pointer rounded-full"
                  style={{
                    width: Math.max(size * 0.45, 20),
                    height: Math.max(size * 0.45, 20),
                    background: 'rgba(0,0,0,0.65)',
                    backdropFilter: 'blur(8px)',
                    border: `1px solid rgba(255,255,255,0.25)`,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
                  }}
                >
                  <MdVisibility size={size >= 48 ? 13 : 10} className="text-white/90" />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Selected check */}
          <AnimatePresence>
            {selected && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={SPRING.snappy}
                className="absolute -bottom-0.5 -right-0.5 w-4.5 h-4.5 rounded-full flex items-center justify-center"
                style={{ background: THEME.primary, boxShadow: THEME.primaryGlow }}
              >
                <MdCheck size={10} className="text-white" />
              </motion.div>
            )}
          </AnimatePresence>
        </button>
        {label && <span className="text-[10px] font-medium text-white/70">{label}</span>}
      </motion.div>

      {/* Preview Modal */}
      <SwatchPreviewModal
        isOpen={previewOpen}
        imageUrl={url}
        label={label}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  );
};

export default SwatchCircle;
